import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';

import {
  Typography,
  CurrencyInputField,
  ToggleButtonGroupField,
  TextField,
  ProductGroupSelectField,
  Select,
} from '@occc-app/design-system';
import { parseNumber } from '@occc-app/lib/finalFormUtils';

import { ProductDiscountAwardType, ProductDiscountFormValues } from '../types';
import { countProductsByOptions } from '../constants';
import { getProductDiscountAwardTypeButtons } from '../utils';

type ProductDiscountConfigurationProps = {
  values: ProductDiscountFormValues;
};

export const ProductDiscountConfiguration: React.FC<ProductDiscountConfigurationProps> = ({
  values,
}) => {
  const awardType = values?.awardType;
  const showCurrencyInputField =
    awardType === ProductDiscountAwardType.Discount ||
    awardType === ProductDiscountAwardType.PriceOverride;
  const currencyInputLabel =
    awardType === ProductDiscountAwardType.Discount ? 'Dollar(s) off' : 'Maximum cost';
  const productDiscountAwardTypeButtons = useMemo(
    () => getProductDiscountAwardTypeButtons(values?.purchaseThreshold),
    [values?.purchaseThreshold],
  );

  return (
    <Grid container spacing={3}>
      <Grid item>
        <Typography variant='subtitle1' gutterBottom>
          Member Receives
        </Typography>
        <ToggleButtonGroupField
          name='productDiscount.awardType'
          buttons={productDiscountAwardTypeButtons}
        />
      </Grid>
      {showCurrencyInputField && (
        <Grid item xs={12} sm={3}>
          <Box mt={3}>
            <CurrencyInputField name='productDiscount.moneyAmount' label={currencyInputLabel} />
          </Box>
        </Grid>
      )}
      <Grid item xs={12} sm={3} md={2}>
        <Typography variant='subtitle1' gutterBottom>
          For Every
        </Typography>
        <TextField
          name='productDiscount.purchaseThreshold'
          label='Items'
          fieldProps={{
            parse: parseNumber,
          }}
        />
      </Grid>

      {awardType === ProductDiscountAwardType.Discount && (
        <Grid item xs={12} sm={5} md={3}>
          <Typography variant='subtitle1' gutterBottom>
            Count Products By
          </Typography>

          <Select
            name='productDiscount.countProductsBy'
            label='Count Products By'
            data={countProductsByOptions}
            formControlProps={{ size: 'medium', margin: 'none' }}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant='subtitle1' gutterBottom>
          On the following product group(s):
        </Typography>
        <ProductGroupSelectField
          inputProps={{
            margin: 'none',
          }}
          name='productDiscount.eligibleProductGroups'
          label='Product groups'
        />
      </Grid>
    </Grid>
  );
};
